import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    PLATFORM_ID,
    Signal,
    WritableSignal,
    afterNextRender,
    computed,
    inject,
    signal,
} from '@angular/core';

import { BlockDirective, ElemDirective } from '@rt-tools/core';
import { IPageBreadcrumb, PageHeaderService } from '@stair-craft/common/util';

import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';

const COMPACT_SCROLL_THRESHOLD: number = 24;
const DEFAULT_HEADER_OFFSET: number = 64;
const BEM_BLOCK: string = 'sc-page-header';

/**
 * Whether the header is folded after a scroll to `scrollY` (SC-SIT-013): it folds past the
 * threshold and unfolds only back at the top, so a scroll the fold itself nudges back keeps it folded.
 */
export function pageHeaderCompact(wasCompact: boolean, scrollY: number): boolean {
    const unfoldAt: number = wasCompact ? 0 : COMPACT_SCROLL_THRESHOLD;
    return scrollY > unfoldAt;
}

/**
 * The room a folded header leaves below itself (SC-SIT-013): what it lost against its unfolded
 * height. With it the page keeps its length when the header folds, so a page only a little
 * longer than the window does not pull the scroll back under the threshold and unfold it again.
 */
export function pageHeaderFoldGap(unfoldedHeight: number, height: number): number {
    return Math.max(0, unfoldedHeight - height);
}

@Component({
    selector: 'sc-page-header',
    templateUrl: './page-header.component.html',
    styleUrl: './page-header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,

        // components
        BreadcrumbsComponent,
    ],
    host: {
        class: BEM_BLOCK,
        '[class.sc-page-header--compact]': 'compact()',
    },
})
export class PageHeaderComponent {
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #host: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

    #onScroll: (() => void) | null = null;
    #resizeObserver: ResizeObserver | null = null;
    #unfoldedHeight: number = 0;

    readonly #breadcrumbs: Signal<readonly IPageBreadcrumb[]> = inject(PageHeaderService).breadcrumbs;
    protected readonly compact: WritableSignal<boolean> = signal<boolean>(false);

    // Breadcrumb row shows only the ancestor path (each crumb followed by a
    // separator, leading into the title). The current page is the title below,
    // never repeated as a crumb.
    protected readonly parentCrumbs: Signal<readonly IPageBreadcrumb[]> = computed((): readonly IPageBreadcrumb[] =>
        this.#breadcrumbs().slice(0, -1)
    );

    // The current page label. In the compact bar it sits inline after the
    // ancestor path ("Главная / Блог"); in the expanded header the same page is
    // the big title below.
    protected readonly currentLabel: Signal<string> = computed((): string => {
        const crumbs: readonly IPageBreadcrumb[] = this.#breadcrumbs();
        return crumbs.length ? crumbs[crumbs.length - 1].label : '';
    });

    constructor() {
        afterNextRender((): void => {
            if (!isPlatformBrowser(this.#platformId)) {
                return;
            }
            this.#trackHeaderOffset();
            this.#onScroll = (): void => {
                this.compact.set(pageHeaderCompact(this.compact(), globalThis.scrollY));
            };
            this.#onScroll();
            globalThis.addEventListener('scroll', this.#onScroll, { passive: true });
        });

        this.#destroyRef.onDestroy((): void => {
            if (this.#onScroll !== null) {
                globalThis.removeEventListener('scroll', this.#onScroll);
            }
            this.#resizeObserver?.disconnect();
            this.#host.nativeElement.ownerDocument.documentElement.style.removeProperty('--sc-sticky-offset');
        });
    }

    // The site header is sticky, so this header must sit directly beneath it.
    // Heights change across breakpoints (nav wraps on mobile) and as this header
    // compacts on scroll, so both are measured live and published as CSS vars:
    //  - `--sc-page-header-offset` (this header's own sticky `top`)
    //  - `--sc-page-header-fold-gap` (the room the folded header keeps below itself)
    //  - `--sc-sticky-offset` (both stacked headers' combined height) so content
    //    below — e.g. the sticky article TOC — can offset from it and not be
    //    clipped behind the headers.
    #trackHeaderOffset(): void {
        const host: HTMLElement = this.#host.nativeElement;
        const appHeader: Element | null = host.ownerDocument.querySelector('.sc-app-header');
        const update: () => void = (): void => {
            const appHeight: number = appHeader instanceof HTMLElement ? appHeader.offsetHeight : DEFAULT_HEADER_OFFSET;
            host.style.setProperty('--sc-page-header-offset', `${String(appHeight)}px`);
            if (!this.compact()) {
                this.#unfoldedHeight = host.offsetHeight;
            }
            const gap: number = pageHeaderFoldGap(this.#unfoldedHeight, host.offsetHeight);
            host.style.setProperty('--sc-page-header-fold-gap', `${String(gap)}px`);
            host.ownerDocument.documentElement.style.setProperty('--sc-sticky-offset', `${String(appHeight + host.offsetHeight)}px`);
        };
        this.#resizeObserver = new ResizeObserver(update);
        if (appHeader instanceof HTMLElement) {
            this.#resizeObserver.observe(appHeader);
        }
        this.#resizeObserver.observe(host);
        update();
    }
}
